import { merge } from 'lodash-es';
import type { PropsWithChildren } from 'react';

import { isValidGroupMode } from './is-valid-group-mode.js';
import { isValidTooltipVariant } from './is-valid-tooltip-variant.js';
import { isValidColorRule } from '../../core/slots/ColorRule.js';
import {
  ColorPalette,
  type ColorPaletteMode,
  CustomColorPalette,
  isCustomColorPalette,
} from '../../core/types/color-palette.js';
import { ValueRepresentation } from '../../core/types/scales.js';
import { isValidCSSColor } from '../../core/utils/getValidColor.js';
import { isValidColorPalette } from '../../core/utils/is-valid-color-palette.js';
import { isValidLegendPosition } from '../../core/utils/is-valid-legend-position.js';
import { isValidValueRepresentation } from '../../core/utils/is-valid-value-representation.js';
import { DEFAULT_SCALE_FALLBACK_COLOR } from '../constants.js';
import { CategoricalBarChartThresholdIndicatorProps } from '../slots/ThresholdIndicator.js';
import {
  CategoricalBarChartConfiguration,
  CategoricalBarChartProps,
  CategoricalBarChartSlots,
  CategoricalBarChartThresholdConfiguration,
  GroupMode,
  InternalCategoricalBarChartThresholdConfiguration,
  Layout,
  type InternalCategoricalBarChartConfig,
} from '../types/categorical-bar-chart.js';
import { defaultConfig } from '../types/defaults.js';
import { isValidLayout } from '../utils/is-valid-layout.js';

/**
 * Builds a ColorPalette configuration based on context and props
 * @param contextColorPalette - color palette context
 * @param propsColorPalette - color palette props
 */
export function getColorPaletteConfiguration(
  contextColorPalette?: ColorPalette | CustomColorPalette,
  propsColorPalette?: ColorPalette | CustomColorPalette,
) {
  const colorPalette =
    propsColorPalette ?? contextColorPalette ?? defaultConfig.colorPalette;

  return isCustomColorPalette(colorPalette) || isValidColorPalette(colorPalette)
    ? colorPalette
    : defaultConfig.colorPalette;
}

/**
 * Decides whether bars use colors sequentially or use only the first color of the color palette
 * @param propsColorPaletteMode - colorPaletteMode from props
 * @param contextColorPaletteMode - colorPaletteMode from context
 */
export function getColorPaletteMode(
  propsColorPaletteMode?: ColorPaletteMode,
  contextColorPaletteMode?: ColorPaletteMode,
) {
  const colorPaletteMode =
    propsColorPaletteMode ??
    contextColorPaletteMode ??
    defaultConfig.colorPaletteMode;

  return colorPaletteMode;
}

/**
 * Builds a Layout configuration based on context and props
 *
 * @param contextLayout - layout context
 * @param propsLayout - layout props
 */
export function getLayoutConfiguration(
  contextLayout?: Layout,
  propsLayout?: Layout,
) {
  const layout = propsLayout ?? contextLayout ?? defaultConfig.layout;

  return isValidLayout(layout) ? layout : defaultConfig.layout;
}

/**
 * Builds a GroupMode configuration based on context and props
 *
 * @param contextGroupMode - group mode context
 * @param propsGroupMode - group mode props
 */
export function getGroupModeConfiguration(
  contextGroupMode?: GroupMode,
  propsGroupMode?: GroupMode,
): GroupMode {
  const groupMode =
    propsGroupMode ?? contextGroupMode ?? defaultConfig.groupMode;

  return isValidGroupMode(groupMode) ? groupMode : defaultConfig.groupMode;
}

/**
 * Builds a ValueRepresentation configuration based on context and props
 *
 * @param contextValueRepresentation - value representation context
 * @param propsValueRepresentation - value representation props
 */
export function getValueRepresentationConfiguration(
  contextValueRepresentation?: ValueRepresentation,
  propsValueRepresentation?: ValueRepresentation,
) {
  const valueRepresentation =
    propsValueRepresentation ??
    contextValueRepresentation ??
    defaultConfig.valueRepresentation;

  return isValidValueRepresentation(valueRepresentation)
    ? valueRepresentation
    : defaultConfig.valueRepresentation;
}

export function getThresholdsConfig(
  contextThresholds: CategoricalBarChartThresholdConfiguration[] = [],
  slotsThresholds: CategoricalBarChartThresholdIndicatorProps[],
): InternalCategoricalBarChartThresholdConfiguration[] {
  return contextThresholds.concat(slotsThresholds).map((threshold) => ({
    ...threshold,
    id: crypto.randomUUID(),
    color: isValidCSSColor(threshold.color)
      ? threshold.color
      : DEFAULT_SCALE_FALLBACK_COLOR,
  }));
}

/**
 * Build categorical bar chart configuration.
 *
 * @param props - config from props
 * @param contextConfig - config from context
 * @param slots - config from components slots
 * @returns RequiredCategoricalBarChartConfiguration
 */
export function buildCategoricalBarChartConfig(
  props: PropsWithChildren<CategoricalBarChartProps>,
  contextConfig: CategoricalBarChartConfiguration,
  slots: CategoricalBarChartSlots,
): InternalCategoricalBarChartConfig {
  const colorPalette = getColorPaletteConfiguration(
    contextConfig.colorPalette,
    props.colorPalette,
  );

  const colorPaletteMode = getColorPaletteMode(
    props.colorPaletteMode,
    contextConfig.colorPaletteMode,
  );

  const layout = getLayoutConfiguration(contextConfig.layout, props.layout);

  const groupMode = getGroupModeConfiguration(
    contextConfig.groupMode,
    props.groupMode,
  );

  const valueRepresentation = getValueRepresentationConfiguration(
    contextConfig.valueRepresentation,
    props.valueRepresentation,
  );

  const categoryAxis = merge(
    {},
    defaultConfig.categoryAxis,
    contextConfig.categoryAxis,
    slots.categoryAxis,
  );
  categoryAxis.tickLabelLayout = isValidLayout(categoryAxis.tickLabelLayout)
    ? categoryAxis.tickLabelLayout
    : defaultConfig.categoryAxis.tickLabelLayout;

  const valueAxis = merge(
    {},
    defaultConfig.valueAxis,
    contextConfig.valueAxis,
    slots.valueAxis,
  );

  const legend = merge(
    {},
    defaultConfig.legend,
    contextConfig.legend,
    slots.legend,
  );
  legend.position = isValidLegendPosition(legend.position)
    ? legend.position
    : defaultConfig.legend.position;
  legend.hidden = Boolean(legend.hidden);

  const tooltip = merge(
    {},
    defaultConfig.tooltip,
    contextConfig.tooltip,
    slots.tooltip,
  );
  tooltip.variant = isValidTooltipVariant(tooltip.variant)
    ? tooltip.variant
    : defaultConfig.tooltip.variant;

  const toolbar = merge(
    {},
    defaultConfig.toolbar,
    contextConfig.toolbar,
    slots.toolbar,
  );

  const truncationMode =
    props.truncationMode ??
    contextConfig.truncationMode ??
    defaultConfig.truncationMode;

  const configThresholds = contextConfig.thresholdIndicators || [];
  const thresholdIndicators = getThresholdsConfig(
    configThresholds,
    slots.thresholds,
  );

  const colorRules = (contextConfig.colorRules ?? [])
    .filter(isValidColorRule)
    .concat(slots.colorRules ?? []);

  return {
    colorPalette,
    colorPaletteMode,
    layout,
    groupMode,
    valueRepresentation,
    valueAxis,
    categoryAxis,
    legend,
    tooltip,
    toolbar,
    truncationMode,
    thresholdIndicators,
    colorRules,
    intents: slots.intents,
  };
}

/**
 * Get chart configuration merging the default one with the stringify json config.
 * In case there is a conflict property, default would prevail.
 * In case there is any parse error. It will return an empty config.
 *
 * @param serializedConfig - stringify json config
 * @returns - CategoricalBarChartConfiguration
 */
export function deserializeConfig(
  serializedConfig: string,
): CategoricalBarChartConfiguration {
  try {
    return merge({}, defaultConfig, JSON.parse(serializedConfig));
  } catch {
    return defaultConfig;
  }
}
